import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
    Lightbulb, ChevronUp, MessageSquare, Search, Plus,
    Loader2, Filter, Clock, TrendingUp, CheckCircle2,
    Rocket, Target, Pencil, Send, Bug, Paperclip
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
    submitted: { label: 'Submitted', color: 'bg-stone-100 text-stone-600', icon: Lightbulb },
    selected: { label: 'Selected', color: 'bg-blue-100 text-blue-700', icon: Target },
    planning: { label: 'Planning', color: 'bg-amber-100 text-amber-700', icon: Pencil },
    in_progress: { label: 'In Progress', color: 'bg-indigo-100 text-indigo-700', icon: Loader2 },
    released: { label: 'Released', color: 'bg-emerald-100 text-emerald-700', icon: Rocket },
};

function StatusBadge({ status }) {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.submitted;
    const Icon = cfg.icon;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color}`} data-testid={`status-badge-${status}`}>
            <Icon className="w-3 h-3" />
            {cfg.label}
        </span>
    );
}

function RequestCard({ req, onVote, onClick }) {
    return (
        <div
            className="bg-white rounded-xl border border-stone-200 p-4 sm:p-5 hover:border-stone-300 hover:shadow-md transition-all cursor-pointer group"
            onClick={() => onClick(req.request_id)}
            data-testid={`request-card-${req.request_id}`}
        >
            <div className="flex gap-3 sm:gap-4">
                {/* Vote button */}
                <button
                    onClick={(e) => { e.stopPropagation(); onVote(req.request_id); }}
                    className={`flex flex-col items-center justify-center min-w-[52px] py-2 rounded-xl border transition-all ${
                        req.user_voted
                            ? 'bg-stone-900 border-stone-900 text-white'
                            : 'bg-white border-stone-200 text-stone-500 hover:border-stone-400 hover:text-stone-700'
                    }`}
                    data-testid={`vote-btn-${req.request_id}`}
                >
                    <ChevronUp className="w-4 h-4" />
                    <span className="text-sm font-bold">{req.vote_count}</span>
                </button>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] font-mono text-stone-400">{req.ticket_number}</span>
                            {req.type === 'bug' && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium flex items-center gap-0.5">
                                    <Bug className="w-3 h-3" /> Bug
                                </span>
                            )}
                            <StatusBadge status={req.status} />
                            {req.priority && req.priority !== 'medium' && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                    req.priority === 'critical' ? 'bg-red-100 text-red-700' :
                                    req.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                                    'bg-stone-100 text-stone-500'
                                }`}>
                                    {req.priority}
                                </span>
                            )}
                            {req.category && req.category !== 'other' && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-50 text-stone-500">{req.category}</span>
                            )}
                            {(req.attachment_count > 0) && (
                                <span className="text-[10px] text-stone-400 flex items-center gap-0.5">
                                    <Paperclip className="w-3 h-3" /> {req.attachment_count}
                                </span>
                            )}
                        </div>
                    </div>
                    <h3 className="font-semibold text-stone-900 text-sm sm:text-base mb-1 group-hover:text-stone-700 transition-colors line-clamp-1">
                        {req.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-stone-500 line-clamp-2 mb-2">{req.description}</p>
                    <div className="flex items-center gap-4 text-xs text-stone-400">
                        <span>{req.author_name}</span>
                        <span className="flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            {req.comment_count}
                        </span>
                        <span>{new Date(req.created_at).toLocaleDateString()}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

function RequestDetail({ requestId, onClose, onVote }) {
    const { user, getAuthHeader } = useAuth();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [comment, setComment] = useState('');
    const [sending, setSending] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/feature-requests/${requestId}`, { headers: getAuthHeader() });
            setDetail(res.data);
        } catch {
            toast.error('Failed to load request');
        } finally {
            setLoading(false);
        }
    }, [requestId, getAuthHeader]);

    useEffect(() => { load(); }, [load]);

    const handleComment = async () => {
        if (!comment.trim()) return;
        setSending(true);
        try {
            await axios.post(`${API}/feature-requests/${requestId}/comments`, { text: comment }, { headers: getAuthHeader() });
            setComment('');
            load();
        } catch {
            toast.error('Failed to post comment');
        } finally {
            setSending(false);
        }
    };

    const handleVote = async () => {
        await onVote(requestId);
        load();
    };

    if (loading) return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="sm:max-w-2xl">
                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
            </DialogContent>
        </Dialog>
    );

    if (!detail) return null;

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0" data-testid="request-detail-modal">
                <div className="p-5 pb-3 border-b border-stone-100">
                    <DialogHeader>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-mono text-stone-400">{detail.ticket_number}</span>
                            {detail.type === 'bug' && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium flex items-center gap-0.5">
                                    <Bug className="w-3 h-3" /> Bug
                                </span>
                            )}
                            <StatusBadge status={detail.status} />
                        </div>
                        <DialogTitle className="text-lg font-bold text-stone-900">{detail.title}</DialogTitle>
                        <DialogDescription className="text-sm text-stone-600 mt-1">{detail.description}</DialogDescription>
                    </DialogHeader>
                    {/* Attachments */}
                    {detail.attachments && detail.attachments.length > 0 && (
                        <div className="mt-3">
                            <p className="text-[10px] font-semibold text-stone-400 uppercase mb-1.5 flex items-center gap-1">
                                <Paperclip className="w-3 h-3" /> Screenshots ({detail.attachments.length})
                            </p>
                            <div className="flex gap-2 flex-wrap">
                                {detail.attachments.map((att) => (
                                    <img key={att.attachment_id} src={att.data_url}
                                        alt="Screenshot" onClick={() => window.open(att.data_url, '_blank')}
                                        className="w-48 h-32 object-cover rounded-lg border border-stone-200 cursor-pointer hover:border-stone-400 hover:shadow-md transition-all" />
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="flex items-center gap-3 mt-3">
                        <button
                            onClick={handleVote}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                detail.user_voted
                                    ? 'bg-stone-900 text-white'
                                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                            }`}
                            data-testid="detail-vote-btn"
                        >
                            <ChevronUp className="w-4 h-4" />
                            {detail.vote_count} vote{detail.vote_count !== 1 ? 's' : ''}
                        </button>
                        <span className="text-xs text-stone-400">by {detail.author_name} on {new Date(detail.created_at).toLocaleDateString()}</span>
                    </div>
                    {detail.voters && detail.voters.length > 0 && (
                        <div className="mt-2 flex items-center gap-1 flex-wrap">
                            <span className="text-[10px] text-stone-400 uppercase font-semibold">Voted by:</span>
                            {detail.voters.slice(0, 10).map(v => (
                                <span key={v.user_id} className="text-[10px] bg-stone-50 px-1.5 py-0.5 rounded text-stone-500">{v.name}</span>
                            ))}
                            {detail.voters.length > 10 && (
                                <span className="text-[10px] text-stone-400">+{detail.voters.length - 10} more</span>
                            )}
                        </div>
                    )}
                </div>

                {/* Comments */}
                <div className="flex-1 overflow-y-auto px-5 py-3">
                    <h4 className="text-xs font-semibold text-stone-400 uppercase mb-3">Discussion ({detail.comments?.length || 0})</h4>
                    {(!detail.comments || detail.comments.length === 0) ? (
                        <p className="text-sm text-stone-400 text-center py-6">No comments yet. Start the discussion!</p>
                    ) : (
                        <div className="space-y-3">
                            {detail.comments.map(c => (
                                <div key={c.comment_id} className={`rounded-lg p-3 ${c.is_system ? 'bg-blue-50 border border-blue-100' : c.is_admin ? 'bg-amber-50 border border-amber-100' : 'bg-stone-50'}`}
                                    data-testid={`comment-${c.comment_id}`}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs font-semibold text-stone-700">{c.user_name}</span>
                                        {c.is_admin && <span className="text-[10px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full font-semibold">Admin</span>}
                                        <span className="text-[10px] text-stone-400">{new Date(c.created_at).toLocaleString()}</span>
                                    </div>
                                    <p className="text-sm text-stone-700">{c.text}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Comment input */}
                <div className="border-t border-stone-100 p-4">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleComment()}
                            placeholder="Add a comment..."
                            className="flex-1 px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10 bg-white"
                            data-testid="comment-input"
                        />
                        <Button
                            onClick={handleComment}
                            disabled={sending || !comment.trim()}
                            className="bg-stone-900 text-white hover:bg-stone-800"
                            data-testid="comment-submit-btn"
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function FeatureRequests() {
    const { getAuthHeader } = useAuth();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [sortBy, setSortBy] = useState('votes');
    const [showCreate, setShowCreate] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState({ title: '', description: '', type: 'feature', priority: 'medium', category: 'other' });
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set('status', statusFilter);
            if (typeFilter) params.set('type', typeFilter);
            params.set('sort', sortBy);
            const res = await axios.get(`${API}/feature-requests/?${params}`, { headers: getAuthHeader() });
            setRequests(Array.isArray(res.data) ? res.data : (res.data?.data || []));
        } catch {
            toast.error('Failed to load feature requests');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader, statusFilter, typeFilter, sortBy]);

    useEffect(() => { load(); }, [load]);

    const handleVote = async (requestId) => {
        try {
            const res = await axios.post(`${API}/feature-requests/${requestId}/vote`, {}, { headers: getAuthHeader() });
            setRequests(prev => prev.map(r =>
                r.request_id === requestId
                    ? { ...r, user_voted: res.data.voted, vote_count: res.data.vote_count }
                    : r
            ));
        } catch {
            toast.error('Failed to vote');
        }
    };

    const handleCreate = async () => {
        if (!form.title.trim() || !form.description.trim()) return;
        setCreating(true);
        try {
            await axios.post(`${API}/feature-requests/`, form, { headers: getAuthHeader() });
            toast.success(form.type === 'bug' ? 'Bug report submitted!' : 'Feature request submitted!');
            setShowCreate(false);
            setForm({ title: '', description: '', type: 'feature', priority: 'medium', category: 'other' });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create request');
        } finally {
            setCreating(false);
        }
    };

    const filtered = requests.filter(r =>
        r.title.toLowerCase().includes(search.toLowerCase()) ||
        r.description.toLowerCase().includes(search.toLowerCase()) ||
        r.ticket_number.toLowerCase().includes(search.toLowerCase())
    );

    const statusCounts = requests.reduce((acc, r) => {
        acc[r.status] = (acc[r.status] || 0) + 1;
        return acc;
    }, {});

    return (
        <div className="space-y-5" data-testid="feature-requests-page">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Feature Requests</h1>
                    <p className="text-sm text-stone-500 mt-0.5">Suggest and vote on features you'd like to see</p>
                </div>
                <Button
                    onClick={() => setShowCreate(true)}
                    className="bg-stone-900 text-white hover:bg-stone-800"
                    data-testid="new-request-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    New Request
                </Button>
            </div>

            {/* Status Filters */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
                <button
                    onClick={() => setStatusFilter('')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${!statusFilter ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
                    data-testid="filter-all"
                >
                    All ({requests.length})
                </button>
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <button
                        key={key}
                        onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${statusFilter === key ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
                        data-testid={`filter-${key}`}
                    >
                        {cfg.label} ({statusCounts[key] || 0})
                    </button>
                ))}
            </div>

            {/* Type Filter */}
            <div className="flex items-center gap-2">
                <button onClick={() => setTypeFilter('')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!typeFilter ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
                    All Types
                </button>
                <button onClick={() => setTypeFilter(typeFilter === 'feature' ? '' : 'feature')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${typeFilter === 'feature' ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
                    <Lightbulb className="w-3 h-3" /> Features
                </button>
                <button onClick={() => setTypeFilter(typeFilter === 'bug' ? '' : 'bug')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${typeFilter === 'bug' ? 'bg-red-100 text-red-700' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
                    <Bug className="w-3 h-3" /> Bugs
                </button>
            </div>

            {/* Search + Sort */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <input
                        type="text"
                        placeholder="Search requests..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                        data-testid="search-requests"
                    />
                </div>
                <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-0.5">
                    <button
                        onClick={() => setSortBy('votes')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${sortBy === 'votes' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500'}`}
                        data-testid="sort-votes"
                    >
                        <TrendingUp className="w-3.5 h-3.5" /> Top Voted
                    </button>
                    <button
                        onClick={() => setSortBy('date')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${sortBy === 'date' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500'}`}
                        data-testid="sort-date"
                    >
                        <Clock className="w-3.5 h-3.5" /> Newest
                    </button>
                </div>
            </div>

            {/* Request List */}
            {loading ? (
                <div className="space-y-3">
                    {[1,2,3].map(i => <div key={i} className="h-28 bg-white rounded-xl border border-stone-200 animate-pulse" />)}
                </div>
            ) : filtered.length === 0 ? (
                <div className="bg-white rounded-xl border border-stone-200 p-12 text-center">
                    <Lightbulb className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                    <p className="text-stone-500 text-sm">No feature requests yet</p>
                    <button onClick={() => setShowCreate(true)} className="mt-2 text-sm text-stone-700 hover:text-stone-900 font-medium">Be the first to submit one</button>
                </div>
            ) : (
                <div className="space-y-3" data-testid="requests-list">
                    {filtered.map(r => (
                        <RequestCard key={r.request_id} req={r} onVote={handleVote} onClick={setSelectedId} />
                    ))}
                </div>
            )}

            {/* Detail Modal */}
            {selectedId && (
                <RequestDetail
                    requestId={selectedId}
                    onClose={() => setSelectedId(null)}
                    onVote={handleVote}
                />
            )}

            {/* Create Modal */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-lg" data-testid="create-request-modal">
                    <DialogHeader>
                        <DialogTitle>Submit Request</DialogTitle>
                        <DialogDescription>Report a bug or request a feature. Others can vote and discuss it.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-2">
                        {/* Type toggle */}
                        <div className="bg-stone-100 rounded-lg p-0.5 flex">
                            <button onClick={() => setForm(p => ({ ...p, type: 'bug' }))}
                                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${form.type === 'bug' ? 'bg-white shadow-sm text-red-600' : 'text-stone-500'}`}>
                                <Bug className="w-3.5 h-3.5" /> Bug Report
                            </button>
                            <button onClick={() => setForm(p => ({ ...p, type: 'feature' }))}
                                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${form.type === 'feature' ? 'bg-white shadow-sm text-amber-600' : 'text-stone-500'}`}>
                                <Lightbulb className="w-3.5 h-3.5" /> Feature Request
                            </button>
                        </div>
                        {/* Priority + Category */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase mb-1">Priority</label>
                                <div className="flex gap-1">
                                    {[{v:'low',l:'Low',c:'bg-stone-100 text-stone-600'},{v:'medium',l:'Med',c:'bg-amber-100 text-amber-700'},{v:'high',l:'High',c:'bg-orange-100 text-orange-700'},{v:'critical',l:'Crit',c:'bg-red-100 text-red-700'}].map(p => (
                                        <button key={p.v} type="button" onClick={() => setForm(f => ({ ...f, priority: p.v }))}
                                            className={`flex-1 px-1.5 py-1.5 text-[10px] font-medium rounded-md transition-colors ${form.priority === p.v ? p.c + ' ring-1 ring-current' : 'bg-stone-50 text-stone-400'}`}>
                                            {p.l}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase mb-1">Category</label>
                                <select value={form.category || 'other'} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                                    className="w-full px-2 py-1.5 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10">
                                    <option value="ui">UI</option><option value="backend">Backend</option>
                                    <option value="performance">Performance</option><option value="integration">Integration</option>
                                    <option value="data">Data</option><option value="other">Other</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase mb-1">Title</label>
                            <input type="text" value={form.title}
                                onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                                placeholder={form.type === 'bug' ? 'Brief description of the bug' : 'Short, clear summary of the feature'}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                                data-testid="request-title-input" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase mb-1">Description</label>
                            <textarea value={form.description}
                                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                                placeholder={form.type === 'bug' ? 'Steps to reproduce, expected vs actual behavior...' : 'Explain the problem this feature solves and how it should work...'}
                                rows={4}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10 resize-none"
                                data-testid="request-description-input" />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                            <Button onClick={handleCreate}
                                disabled={creating || form.title.length < 5 || form.description.length < 10}
                                className={form.type === 'bug' ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-stone-900 text-white hover:bg-stone-800'}
                                data-testid="submit-request-btn">
                                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : form.type === 'bug' ? <Bug className="w-4 h-4 mr-2" /> : <Lightbulb className="w-4 h-4 mr-2" />}
                                {form.type === 'bug' ? 'Submit Bug Report' : 'Submit Request'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
